import React from "react";
import { AlertTriangle } from "@untitledui/icons";

import type { SpendCapState } from "../../hooks/useSpendCap";

interface Props {
  spendCap: SpendCapState;
}

/**
 * System-generated "monthly limit reached" banner.
 *
 * Deliberately NOT an Announcement row: it has to appear and disappear on its
 * own, driven by the user's own spend, with no admin creating or retiring it.
 * It renders in the same slot as `AnnouncementBanner` so it lands where
 * clinicians already look for app-level notices, and borrows its shape.
 *
 * Not dismissible, unlike announcements. Dismissing a "you cannot start a
 * session" notice would leave the user with no explanation the next time
 * recording is refused, the banner IS the explanation, and it clears itself
 * the moment the cap resets or an admin raises it.
 */
export const SpendCapBanner: React.FC<Props> = ({ spendCap }) => {
  const resetLabel = spendCap.resets_at
    ? new Date(spendCap.resets_at).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 16px",
        fontSize: 13,
        fontWeight: 500,
        background: "#fef2f2",
        color: "#991b1b",
        borderBottom: "1px solid #fecaca",
      }}
      role="alert"
      aria-live="assertive"
    >
      <AlertTriangle className="w-5 h-5" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          Monthly usage limit reached
        </div>
        <div style={{ opacity: 0.95 }}>
          New recordings are paused
          {resetLabel ? ` until your limit resets on ${resetLabel}` : ""}.
          Upgrade your plan to continue, or contact support if you believe this
          is a mistake.
        </div>
      </div>
    </div>
  );
};
